import {
  DEFAULT_MODELS_CATALOG_URL,
  type LoadCatalogOptions,
} from "./loadCatalogSources.js";
import { loadCatalogSourcesCached } from "./catalogLoadCache.js";
import { AiModelsCatalogClient } from "./AiModelsCatalogClient.js";

export type CatalogRefreshOptions = LoadCatalogOptions & {
  cacheKey?: string;
  cacheTtlMs?: number;
};

export type CatalogRefreshResult = {
  ok: true;
  directCount: number;
  openRouterCount: number;
  directSource: "remote" | "bundled";
  openRouterSource: "remote" | "bundled";
};

/** Fetch remote catalogs via ai-profiles and warm the in-memory cache. */
export async function refreshAiModelsCatalog(
  options: CatalogRefreshOptions = {},
): Promise<CatalogRefreshResult> {
  const client = new AiModelsCatalogClient(options);
  await client.refresh();
  const loaded = await loadCatalogSourcesCached(options, {
    cacheKey: options.cacheKey,
    ttlMs: options.cacheTtlMs,
    forceRefresh: false,
  });
  return {
    ok: true,
    directCount: loaded.meta.directCount,
    openRouterCount: loaded.meta.openRouterCount,
    directSource: loaded.meta.directSource,
    openRouterSource: loaded.meta.openRouterSource,
  };
}

export type CatalogVerifyOptions = LoadCatalogOptions & {
  cacheKey?: string;
  cacheTtlMs?: number;
};

export type CatalogVerifyReport = {
  ok: boolean;
  directCount: number;
  openRouterCount: number;
  directSource: "remote" | "bundled";
  openRouterSource: "remote" | "bundled";
  catalogUrl: string;
  /** @deprecated Same as {@link catalogUrl}. */
  directUrl: string;
  /** @deprecated Same as {@link catalogUrl}. */
  openRouterUrl: string;
};

/** Load and validate the unified ai-profiles catalog (uses cache unless `forceRefresh`). */
export async function verifyAiModelsCatalog(
  options: CatalogVerifyOptions & { forceRefresh?: boolean } = {},
): Promise<CatalogVerifyReport> {
  const loaded = await loadCatalogSourcesCached(options, {
    cacheKey: options.cacheKey,
    ttlMs: options.cacheTtlMs,
    forceRefresh: options.forceRefresh ?? options.bundledOnly === true,
  });
  const catalogUrl = DEFAULT_MODELS_CATALOG_URL;
  return {
    ok: loaded.direct.size > 0 && loaded.openrouter.size > 0,
    directCount: loaded.meta.directCount,
    openRouterCount: loaded.meta.openRouterCount,
    directSource: loaded.meta.directSource,
    openRouterSource: loaded.meta.openRouterSource,
    catalogUrl,
    directUrl: catalogUrl,
    openRouterUrl: catalogUrl,
  };
}
